"use client"

import { BreadcrumbJsonLd } from "@/components/seo/json-ld"
import { ChevronRight, Home } from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"

type BreadcrumbSegment = {
	name: string
	href: string
}

const SEGMENT_LABELS: Record<string, string> = {
	docs: "Documentation",
	integrations: "Integrations",
	alerts: "Alert Channels",
	sdk: "SDK Reference",
	api: "API Reference",
	quickstart: "Quickstart",
	pricing: "Pricing",
	cron: "Cron Generator",
	"node-js": "Node.js",
	typescript: "TypeScript",
	python: "Python",
	go: "Go",
	php: "PHP",
	laravel: "Laravel",
	rails: "Ruby on Rails",
	bash: "Bash",
	docker: "Docker",
	slack: "Slack",
	discord: "Discord",
	pagerduty: "PagerDuty",
	email: "Email",
	webhook: "Webhook",
}

function getSegmentLabel(segment: string): string {
	return SEGMENT_LABELS[segment] ?? segment.replace(/-/g, " ")
}

export function Breadcrumbs() {
	const pathname = usePathname()

	if (pathname === "/" || pathname === "/docs") return null

	const segments = pathname.split("/").filter(Boolean)
	const breadcrumbs: BreadcrumbSegment[] = [{ name: "Home", href: "/" }]

	let currentPath = ""
	for (const segment of segments) {
		currentPath += `/${segment}`
		breadcrumbs.push({
			name: getSegmentLabel(segment),
			href: currentPath,
		})
	}

	return (
		<>
			<BreadcrumbJsonLd items={breadcrumbs} />
			<nav aria-label="Breadcrumb" className="mb-6">
				<ol className="flex items-center gap-1 text-sm text-muted-foreground">
					{breadcrumbs.map((crumb, index) => {
						const isLast = index === breadcrumbs.length - 1
						return (
							<li key={crumb.href} className="flex items-center gap-1">
								{index > 0 && (
									<ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
								)}
								{isLast ? (
									<span className="text-foreground font-medium truncate max-w-[200px]">
										{crumb.name === "Home" ? (
											<Home className="h-3.5 w-3.5" />
										) : (
											crumb.name
										)}
									</span>
								) : (
									<Link
										href={crumb.href}
										className="hover:text-foreground transition-colors truncate max-w-[150px]"
									>
										{crumb.name === "Home" ? (
											<Home className="h-3.5 w-3.5" />
										) : (
											crumb.name
										)}
									</Link>
								)}
							</li>
						)
					})}
				</ol>
			</nav>
		</>
	)
}
